import {
  BatchableSprite,
  Batcher,
  Buffer,
  BufferUsage,
  ExtensionType,
  Geometry,
  GlBatchAdaptor,
  GlProgram,
  Shader,
  Texture,
  extensions,
  getBatchSamplersUniformGroup,
  type BatchableMeshElement,
  type BatchableQuadElement,
  type BatcherOptions,
  type BatcherPipe,
  type InstructionSet,
  type Renderer,
  type WebGLRenderer,
} from 'pixi.js';
import {
  decodeUoHue,
  HUE_INDEX_MASK,
  PARTIAL_HUE_CHROMA_THRESHOLD,
  PARTIAL_HUE_FLAG,
  SPECTRAL_HUE_FLAG,
} from '../uoHue';
import type { HueTable } from './hueTable';
import { UO_HUE_SPRITE_PIPE_ID } from './uoHueSprite';
import type { UoHueSprite } from './uoHueSprite';

export { UoHueSprite } from './uoHueSprite';

export const UO_HUE_BATCHER_NAME = 'uoHue';
export const UO_HUE_VERTEX_SIZE = 9;

type ConfiguredHueTable = HueTable;

let configuredHueTable: ConfiguredHueTable | undefined;
const liveBatchers = new Set<UoHueBatcher>();

/**
 * Configure the page-shared hue ramp used by every renderer-owned hue batcher.
 *
 * Pixi constructs batcher extensions with only a texture-capacity option, so the
 * loaded ramp is registered before the first hue sprite reaches a render pass.
 * Each Pixi renderer still owns and destroys its own batcher geometry, buffers,
 * and shader.
 */
export function configureUoHueBatch(table: HueTable): void {
  configuredHueTable = table;
  for (const batcher of liveBatchers) batcher.setHueTable(table);
}

export interface UoHueVertex {
  rowY: number;
  mode: 0 | 1 | 2 | 3;
}

/**
 * Convert an encoded UO hue into the two values copied to every sprite vertex.
 *
 * Mode 0 is unhued, 1 is full, 2 is partial, and 3 is spectral.
 * Palette IDs beyond the loaded table retain the filter path's row-one fallback.
 */
export function uoHueVertex(
  encodedHue: number,
  partialHue: boolean,
  hueCount: number,
): UoHueVertex {
  const decoded = decodeUoHue(encodedHue, partialHue);
  const paletteHue = decoded.paletteHue > hueCount ? 1 : decoded.paletteHue;
  if (decoded.spectral) {
    return {
      rowY: paletteHue > 0 ? (paletteHue - 0.5) / hueCount : 0,
      mode: 3,
    };
  }
  if (paletteHue <= 0) return { rowY: 0, mode: 0 };
  return {
    rowY: (paletteHue - 0.5) / hueCount,
    mode: decoded.partial ? 2 : 1,
  };
}

class UoHueBatchGeometry extends Geometry {
  constructor() {
    const attributeBuffer = new Buffer({
      data: new Float32Array(1),
      label: 'uo-hue-attribute-batch-buffer',
      usage: BufferUsage.VERTEX | BufferUsage.COPY_DST,
      shrinkToFit: false,
    });
    const indexBuffer = new Buffer({
      data: new Uint32Array(1),
      label: 'uo-hue-index-batch-buffer',
      usage: BufferUsage.INDEX | BufferUsage.COPY_DST,
      shrinkToFit: false,
    });
    const stride = UO_HUE_VERTEX_SIZE * 4;
    super({
      attributes: {
        aPosition: {
          buffer: attributeBuffer,
          format: 'float32x2',
          stride,
          offset: 0,
        },
        aUV: {
          buffer: attributeBuffer,
          format: 'float32x2',
          stride,
          offset: 2 * 4,
        },
        aColor: {
          buffer: attributeBuffer,
          format: 'unorm8x4',
          stride,
          offset: 4 * 4,
        },
        aTextureIdAndRound: {
          buffer: attributeBuffer,
          format: 'uint16x2',
          stride,
          offset: 5 * 4,
        },
        aUoHue: {
          buffer: attributeBuffer,
          format: 'float32x3',
          stride,
          offset: 6 * 4,
        },
      },
      indexBuffer,
    });
  }
}

function textureSampleSource(maxTextures: number): string {
  const branches: string[] = [];
  for (let index = 0; index < maxTextures; index += 1) {
    const prefix = index === 0 ? '' : 'else ';
    const condition = index < maxTextures - 1
      ? `if (vTextureId < ${index}.5)`
      : '';
    branches.push(`${prefix}${condition} {
      outColor = texture(uTextures[${index}], vUV);
    }`);
  }
  return branches.join('\n');
}

function batchVertexSource(): string {
  return /* glsl */ `
    in vec2 aPosition;
    in vec2 aUV;
    in vec4 aColor;
    in vec2 aTextureIdAndRound;
    in vec3 aUoHue;

    out vec4 vColor;
    out vec2 vUV;
    out float vTextureId;
    out float vHueRowY;
    out float vHueMode;
    out float vFlashMix;

    uniform mat3 uProjectionMatrix;
    uniform mat3 uWorldTransformMatrix;
    uniform vec4 uWorldColorAlpha;
    uniform vec2 uResolution;

    vec2 roundPixels(vec2 position, vec2 targetSize) {
      return (floor(((position * 0.5 + 0.5) * targetSize) + 0.5) / targetSize) * 2.0 - 1.0;
    }

    void main(void) {
      mat3 modelViewProjectionMatrix = uProjectionMatrix * uWorldTransformMatrix;
      gl_Position = vec4(
        (modelViewProjectionMatrix * vec3(aPosition, 1.0)).xy,
        0.0,
        1.0
      );
      if (aTextureIdAndRound.x == 1.0) {
        gl_Position.xy = roundPixels(gl_Position.xy, uResolution);
      }
      vUV = aUV;
      vColor = vec4(aColor.rgb * aColor.a, aColor.a) * uWorldColorAlpha;
      vTextureId = aTextureIdAndRound.y;
      vHueRowY = aUoHue.x;
      vHueMode = aUoHue.y;
      vFlashMix = aUoHue.z;
    }
  `;
}

function batchFragmentSource(maxTextures: number): string {
  return /* glsl */ `
    in vec4 vColor;
    in vec2 vUV;
    in float vTextureId;
    in float vHueRowY;
    in float vHueMode;
    in float vFlashMix;

    out vec4 finalColor;

    uniform sampler2D uTextures[${maxTextures}];
    uniform sampler2D uHueRamp;
    uniform float uPartialThreshold;

    void main(void) {
      vec4 outColor;
      ${textureSampleSource(maxTextures)}
      outColor *= vColor;

      if (vHueMode > 2.5) {
        float alpha = outColor.a * clamp(1.0 - outColor.r * 1.5, 0.0, 1.0);
        outColor = vec4(0.0, 0.0, 0.0, alpha);
      } else if (vHueMode > 0.5 && outColor.a >= 0.001) {
        bool keepColor = false;
        if (vHueMode > 1.5) {
          float chroma =
            max(max(outColor.r, outColor.g), outColor.b) -
            min(min(outColor.r, outColor.g), outColor.b);
          keepColor = chroma > uPartialThreshold;
        }
        if (!keepColor) {
          float lum = clamp(
            dot(outColor.rgb, vec3(0.299, 0.587, 0.114)),
            0.0,
            1.0
          );
          float index = (floor(lum * 31.0) + 0.5) / 32.0;
          vec3 hued = texture(uHueRamp, vec2(index, vHueRowY)).rgb;
          outColor = vec4(hued, outColor.a);
        }
      }

      outColor.rgb = mix(outColor.rgb, vec3(outColor.a), vFlashMix);
      finalColor = outColor;
    }
  `;
}

class UoHueBatchShader extends Shader {
  hueRamp;
  readonly hueTextureUnit: number;

  constructor(maxTextures: number, hueTable: HueTable) {
    super({
      glProgram: GlProgram.from({
        name: 'uo-hue-batch',
        vertex: batchVertexSource(),
        fragment: batchFragmentSource(maxTextures),
      }),
      resources: {
        batchSamplers: getBatchSamplersUniformGroup(maxTextures),
        hueUniforms: {
          uHueRamp: { value: maxTextures, type: 'i32' },
          uPartialThreshold: {
            value: PARTIAL_HUE_CHROMA_THRESHOLD,
            type: 'f32',
          },
        },
      },
    });
    this.hueRamp = hueTable.source;
    this.hueTextureUnit = maxTextures;
  }
}

interface UoHueBatchElement {
  readonly renderable: {
    readonly hue?: number;
    readonly partialHue?: boolean;
    readonly flashMix?: number;
  };
  readonly transform: UoHueSprite['groupTransform'];
  readonly color: number;
  readonly roundPixels: 0 | 1;
}

/**
 * Pixi Batcher for ordinary quads carrying optional UO hue data.
 *
 * One texture unit is reserved for the shared hue ramp, leaving fifteen sprite
 * textures on the common sixteen-unit WebGL limit.
 * Ordinary world sprites use UoHueSprite with mode zero, so depth interleaving
 * between world art and hued character layers does not switch programs or
 * split batches.
 */
export class UoHueBatcher extends Batcher {
  static extension = {
    type: [ExtensionType.Batcher],
    name: UO_HUE_BATCHER_NAME,
  } as const;

  readonly name = UO_HUE_BATCHER_NAME;
  readonly geometry = new UoHueBatchGeometry();
  readonly shader: UoHueBatchShader;
  protected readonly vertexSize = UO_HUE_VERTEX_SIZE;
  private hueCount: number;

  /**
   * The three hue values of the element currently being packed, resolved once by
   * {@link readElementHue} and copied to each of its vertices.
   *
   * Packing runs once per quad per batch build, and a batch rebuild re-packs
   * every visible quad, so this is the client's hottest loop and its largest
   * potential garbage source (PRD #826). Pixi's stock packer allocates nothing,
   * and these three instance slots keep this one matching it: the decode writes
   * scalars here instead of returning a fresh object per quad. They are scratch,
   * valid only for the duration of one pack call, and each renderer owns its own
   * batcher so there is no sharing across renderers.
   */
  private hueRowY = 0;
  private hueMode = 0;
  private hueFlashMix = 0;

  constructor(options: BatcherOptions) {
    if (options.maxTextures < 2) {
      throw new Error('UO hue batching requires two WebGL texture units');
    }
    super({ ...options, maxTextures: options.maxTextures - 1 });
    const hueTable = configuredHueTable ?? {
      source: Texture.EMPTY.source,
      count: 1,
    };
    this.hueCount = hueTable.count;
    this.shader = new UoHueBatchShader(this.maxTextures, hueTable);
    liveBatchers.add(this);
  }

  setHueTable(table: HueTable): void {
    this.hueCount = table.count;
    this.shader.hueRamp = table.source;
  }

  /**
   * Resolve one element's hue row, mode, and flash mix into the pack scratch.
   *
   * This is {@link uoHueVertex} inlined against {@link decodeUoHue} so a pack
   * allocates neither the decoded-mode object nor the vertex object. The decision
   * order is deliberately identical: an out-of-range palette id falls back to row
   * one exactly as the filter path did, spectral wins over partial, and palette
   * zero is unhued mode zero.
   */
  private readElementHue(element: BatchableQuadElement | BatchableMeshElement): void {
    const renderable = (element as unknown as UoHueBatchElement).renderable;
    const encodedHue = renderable.hue ?? 0;
    const count = this.hueCount;
    const index = encodedHue & HUE_INDEX_MASK;
    const paletteHue = index > count ? 1 : index;
    if ((encodedHue & SPECTRAL_HUE_FLAG) !== 0) {
      this.hueRowY = paletteHue > 0 ? (paletteHue - 0.5) / count : 0;
      this.hueMode = 3;
    } else if (paletteHue <= 0) {
      this.hueRowY = 0;
      this.hueMode = 0;
    } else {
      this.hueRowY = (paletteHue - 0.5) / count;
      const partial = (renderable.partialHue ?? false) || (encodedHue & PARTIAL_HUE_FLAG) !== 0;
      this.hueMode = partial ? 2 : 1;
    }
    this.hueFlashMix = renderable.flashMix ?? 0;
  }

  packAttributes(
    element: BatchableMeshElement,
    float32View: Float32Array,
    uint32View: Uint32Array,
    index: number,
    textureId: number,
  ): void {
    const hueElement = element as unknown as UoHueBatchElement;
    const textureIdAndRound = textureId << 16 | hueElement.roundPixels;
    const transform = hueElement.transform;
    const a = transform.a;
    const b = transform.b;
    const c = transform.c;
    const d = transform.d;
    const tx = transform.tx;
    const ty = transform.ty;
    const positions = element.positions;
    const uvs = element.uvs;
    const color = hueElement.color;
    this.readElementHue(element);
    const rowY = this.hueRowY;
    const mode = this.hueMode;
    const flashMix = this.hueFlashMix;
    const end = element.attributeOffset + element.attributeSize;
    for (let vertex = element.attributeOffset; vertex < end; vertex += 1) {
      const vertex2 = vertex * 2;
      const x = positions[vertex2]!;
      const y = positions[vertex2 + 1]!;
      float32View[index++] = a * x + c * y + tx;
      float32View[index++] = d * y + b * x + ty;
      float32View[index++] = uvs[vertex2]!;
      float32View[index++] = uvs[vertex2 + 1]!;
      uint32View[index++] = color;
      uint32View[index++] = textureIdAndRound;
      float32View[index++] = rowY;
      float32View[index++] = mode;
      float32View[index++] = flashMix;
    }
  }

  /**
   * Pack one sprite quad, corner by corner, allocating nothing.
   *
   * The four corners are written out longhand rather than looped over a corner
   * table, matching Pixi's stock `DefaultBatcher.packQuadAttributes`. Every quad
   * in the scene passes through here on each batch rebuild, so a per-quad corner
   * array (and the per-corner destructuring it forces) is measurable garbage at
   * Arena density.
   */
  packQuadAttributes(
    element: BatchableQuadElement,
    float32View: Float32Array,
    uint32View: Uint32Array,
    index: number,
    textureId: number,
  ): void {
    const hueElement = element as unknown as UoHueBatchElement;
    const transform = hueElement.transform;
    const a = transform.a;
    const b = transform.b;
    const c = transform.c;
    const d = transform.d;
    const tx = transform.tx;
    const ty = transform.ty;
    const bounds = element.bounds;
    const minX = bounds.minX;
    const minY = bounds.minY;
    const maxX = bounds.maxX;
    const maxY = bounds.maxY;
    const uvs = element.texture.uvs;
    const color = hueElement.color;
    const textureIdAndRound = textureId << 16 | hueElement.roundPixels;
    this.readElementHue(element);
    const rowY = this.hueRowY;
    const mode = this.hueMode;
    const flashMix = this.hueFlashMix;

    float32View[index + 0] = a * minX + c * minY + tx;
    float32View[index + 1] = d * minY + b * minX + ty;
    float32View[index + 2] = uvs.x0;
    float32View[index + 3] = uvs.y0;
    uint32View[index + 4] = color;
    uint32View[index + 5] = textureIdAndRound;
    float32View[index + 6] = rowY;
    float32View[index + 7] = mode;
    float32View[index + 8] = flashMix;

    float32View[index + 9] = a * maxX + c * minY + tx;
    float32View[index + 10] = d * minY + b * maxX + ty;
    float32View[index + 11] = uvs.x1;
    float32View[index + 12] = uvs.y1;
    uint32View[index + 13] = color;
    uint32View[index + 14] = textureIdAndRound;
    float32View[index + 15] = rowY;
    float32View[index + 16] = mode;
    float32View[index + 17] = flashMix;

    float32View[index + 18] = a * maxX + c * maxY + tx;
    float32View[index + 19] = d * maxY + b * maxX + ty;
    float32View[index + 20] = uvs.x2;
    float32View[index + 21] = uvs.y2;
    uint32View[index + 22] = color;
    uint32View[index + 23] = textureIdAndRound;
    float32View[index + 24] = rowY;
    float32View[index + 25] = mode;
    float32View[index + 26] = flashMix;

    float32View[index + 27] = a * minX + c * maxY + tx;
    float32View[index + 28] = d * maxY + b * minX + ty;
    float32View[index + 29] = uvs.x3;
    float32View[index + 30] = uvs.y3;
    uint32View[index + 31] = color;
    uint32View[index + 32] = textureIdAndRound;
    float32View[index + 33] = rowY;
    float32View[index + 34] = mode;
    float32View[index + 35] = flashMix;
  }

  override destroy(): void {
    liveBatchers.delete(this);
    super.destroy({ shader: true });
  }
}

class UoHueSpritePipe {
  static extension = {
    type: [ExtensionType.WebGLPipes],
    name: UO_HUE_SPRITE_PIPE_ID,
  } as const;

  /**
   * Pixi exports BatchableSprite but marks it internal.
   * These calls mirror Pixi 8.19.0 SpritePipe because no public render-pipe API
   * bridges a Sprite to a named custom Batcher.
   * Keep Pixi pinned and revalidate this bridge before an upgrade.
   */
  private readonly batchables = new WeakMap<UoHueSprite, BatchableSprite>();

  constructor(private renderer: Renderer) {}

  addRenderable(sprite: UoHueSprite, instructionSet: InstructionSet): void {
    const batchable = this.getBatchable(sprite);
    if (sprite.didViewUpdate) this.updateBatchable(sprite, batchable);
    this.renderer.renderPipes.batch.addToBatch(batchable, instructionSet);
  }

  updateRenderable(sprite: UoHueSprite): void {
    const batchable = this.getBatchable(sprite);
    if (sprite.didViewUpdate) this.updateBatchable(sprite, batchable);
    batchable._batcher.updateElement(batchable);
  }

  validateRenderable(sprite: UoHueSprite): boolean {
    const batchable = this.getBatchable(sprite);
    return !batchable._batcher.checkAndUpdateTexture(
      batchable,
      sprite.texture,
    );
  }

  private getBatchable(sprite: UoHueSprite): BatchableSprite {
    const existing = this.batchables.get(sprite);
    if (existing) return existing;
    const batchable = new BatchableSprite();
    batchable.batcherName = UO_HUE_BATCHER_NAME;
    batchable.renderable = sprite;
    batchable.transform = sprite.groupTransform;
    batchable.texture = sprite.texture;
    batchable.bounds = sprite.visualBounds;
    batchable.roundPixels = this.renderer.roundPixels || sprite.roundPixels ? 1 : 0;
    this.batchables.set(sprite, batchable);
    return batchable;
  }

  private updateBatchable(
    sprite: UoHueSprite,
    batchable: BatchableSprite,
  ): void {
    batchable.bounds = sprite.visualBounds;
    batchable.texture = sprite.texture;
  }

  destroy(): void {
    this.renderer = null as unknown as Renderer;
  }
}

/**
 * Pixi's stock WebGL batch adaptor binds only the textures named by a batch.
 * The hue shader reserves the next unit for its page-shared ramp.
 */
class UoHueGlBatchAdaptor extends GlBatchAdaptor {
  static override extension = {
    type: [ExtensionType.WebGLPipesAdaptor],
    name: 'batch',
  } as const;

  override start(
    batchPipe: BatcherPipe,
    geometry: Geometry,
    shader: Shader,
  ): void {
    super.start(batchPipe, geometry, shader);
    if (!(shader instanceof UoHueBatchShader)) return;
    const renderer = batchPipe.renderer as WebGLRenderer;
    renderer.texture.bind(shader.hueRamp, shader.hueTextureUnit);
  }
}

extensions.remove(GlBatchAdaptor);
extensions.add(UoHueGlBatchAdaptor, UoHueBatcher, UoHueSpritePipe);
